import React, { useState, useEffect, useMemo, useRef } from "react";
import { Search, FileText, Megaphone, X, MessageSquare, FolderOpen, Package } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useChat } from "../contexts/ChatContext";
import { useAuth } from "../contexts/AuthContext";
import CustomSelect from "./CustomSelect";
import { ragApi } from "../services/api";
import { useModal } from "../hooks/useModal";

const SimilaritySearch = () => {
  const { translate } = useLanguage();
  const { chats, selectedChatId, setSelectedChatId, loading: chatsLoading, chatsLoaded, loadChats } = useChat();
  const { isPlatformOnlySuperAdmin } = useAuth();
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(4);
  const [selectedCollection, setSelectedCollection] = useState(
    "general_information"
  );
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [rawSearch, setRawSearch] = useState(false);

  // Product search filters
  const [maxPrice, setMaxPrice] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [brands, setBrands] = useState("");
  const [availability, setAvailability] = useState("");
  const [pricePreference, setPricePreference] = useState("");

  // Chat switching loading
  const [switchingChat, setSwitchingChat] = useState(false);
  const prevChatId = useRef(selectedChatId);

  // Modal state
  const [selectedResult, setSelectedResult] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Load chats when component mounts (only for admins, not super admins)
  useEffect(() => {
    if (!isPlatformOnlySuperAdmin() && !chatsLoaded && !chatsLoading) {
      loadChats(); // Backend will get tenant_id from JWT
    }
  }, [isPlatformOnlySuperAdmin, chatsLoaded, chatsLoading, loadChats]);

  // Detect chat switching — reset search results
  useEffect(() => {
    if (selectedChatId && prevChatId.current && selectedChatId !== prevChatId.current) {
      setSwitchingChat(true);
      setResults([]);
      setHasSearched(false);
      setError(null);
      const timer = setTimeout(() => setSwitchingChat(false), 600);
      prevChatId.current = selectedChatId;
      return () => clearTimeout(timer);
    }
    prevChatId.current = selectedChatId;
  }, [selectedChatId]);

  // Check if selected chat is internal
  const selectedChat = useMemo(() => {
    if (!selectedChatId || !chats || chats.length === 0) return null;
    return chats.find(chat => chat.id === selectedChatId);
  }, [selectedChatId, chats]);

  const isInternalChat = selectedChat?.internal_chat === true;

  // Reset selected collection if it's internal_procedures but chat is not internal
  useEffect(() => {
    if (selectedCollection === "internal_procedures" && !isInternalChat) {
      setSelectedCollection("general_information");
    }
  }, [isInternalChat, selectedCollection]);

  // Reset results when collection changes
  useEffect(() => {
    setResults([]);
    setHasSearched(false);
    setError(null);
  }, [selectedCollection]);

  // Base collections available for all chats
  const baseCollections = useMemo(() => [
    {
      id: "general_information",
      name: translate("generalInformation") || "General Information",
      icon: FileText,
      description:
        translate("generalInformationDescription") ||
        "Search through general company information and policies",
    },
    {
      id: "campaigns",
      name: translate("campaigns") || "Campaigns",
      icon: Megaphone,
      description:
        translate("campaignsDescription") ||
        "Search through marketing campaigns and promotional content",
    },
    {
      id: "products",
      name: translate("products") || "Products",
      icon: Package,
      description:
        translate("productsSearchDescription") ||
        "Search through product catalog using natural language",
    },
  ], [translate]);

  // Add internal_procedures collection if internal chat is selected
  const collections = useMemo(() => {
    if (isInternalChat) {
      return [
        ...baseCollections,
        {
          id: "internal_procedures",
          name: translate("internalProcedures") || "Internal Procedures",
          icon: FolderOpen,
          description:
            translate("internalProceduresDescription") ||
            "Search through internal procedures and administrative information",
        },
      ];
    }
    return baseCollections;
  }, [isInternalChat, baseCollections, translate]);

  const handleSearch = async () => {
    if (!query.trim()) {
      setError(translate("pleaseEnterQuery") || "Please enter a search query");
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults([]);
    setHasSearched(true);

    try {
      let response;
      if (selectedCollection === "products") {
        const searchData = {
          query: query.trim(),
          limit: limit,
          raw_search: rawSearch,
        };
        // Include filters only for non-raw search
        if (!rawSearch) {
          if (maxPrice) searchData.max_price = parseFloat(maxPrice);
          if (minPrice) searchData.min_price = parseFloat(minPrice);
          if (brands.trim()) searchData.brands = brands.split(",").map(b => b.trim()).filter(Boolean);
          if (availability) searchData.availability = availability;
          if (pricePreference) searchData.price_preference = pricePreference;
        }
        response = await ragApi.productsSimilaritySearch(searchData, selectedChatId);
      } else {
        response = await ragApi.similaritySearch({
          query: query.trim(),
          limit: limit,
          collection: selectedCollection,
        }, selectedChatId);
      }

      if (response && response.results) {
        setResults(response.results);
      } else {
        setError(translate("noResultsFound") || "No results found");
      }
    } catch (err) {
      console.error("Search error:", err);
      setError(
        err.response?.data?.detail ||
          err.message ||
          translate("searchError") ||
          "An error occurred during search"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  // Modal functions
  const handleResultClick = (result) => {
    setSelectedResult(result);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
  };

  const modalRef = useModal(showModal, handleCloseModal);

  const formatScore = (score) => {
    return (score * 100).toFixed(1);
  };

  const truncateText = (text, maxLength = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  /* ─── Wait for chats to be ready before rendering ─── */
  const isChatsReady = isPlatformOnlySuperAdmin() || (chatsLoaded && !chatsLoading);
  if (!isPlatformOnlySuperAdmin() && !isChatsReady) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 animate-pulse">
        <div className="card rounded-xl border border-[var(--color-border)] p-6">
          <div className="h-7 w-52 rounded-lg mb-2 bg-[var(--color-skeleton)]" />
          <div className="h-4 w-80 rounded bg-[var(--color-skeleton-light)]" />
          <div className="mt-8 space-y-4">
            <div className="h-12 rounded-xl bg-[var(--color-skeleton-light)]" />
            <div className="h-12 rounded-xl bg-[var(--color-skeleton-light)]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-h-screen overflow-y-auto">
      <div className="card rounded-xl border border-[var(--color-border)] p-6 relative">
        {/* Chat switching overlay */}
        {switchingChat && (
          <>
            <div className="absolute inset-0 rounded-xl z-10" style={{ backgroundColor: "var(--color-bg)", opacity: 0.7 }} />
            <div className="absolute inset-0 rounded-xl z-10 flex items-center justify-center">
              <div className="flex items-center gap-3 px-4 py-2 rounded-full" style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)" }}>
                <div style={{ width: '16px', height: '16px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <span className="text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>
                  {translate("switchingChat") || "Switching chat..."}
                </span>
              </div>
            </div>
          </>
        )}
        {/* Header with Chat Selector */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold mb-2" style={{ color: "var(--color-text-primary)" }}>
                {translate("similaritySearch") || "Similarity Search"}
              </h1>
              <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
                {translate("similaritySearchDescription") ||
                  "Search for similar content using natural language queries"}
              </p>
            </div>

            {/* Chat Selector - Only show if user has access to chats and more than 1 active chat */}
            {!isPlatformOnlySuperAdmin() && chats && Array.isArray(chats) && chats.filter(chat => chat && chat.is_active).length > 1 && (
              <CustomSelect
                value={selectedChatId || ""}
                onChange={(val) => setSelectedChatId(val)}
                label={translate("selectChat") || "Select Chat"}
                icon={MessageSquare}
                disabled={chatsLoading}
                className="w-56"
                options={chats
                  .filter((chat) => chat.is_active)
                  .sort((a, b) => (a.chatwoot_inbox_id || 0) - (b.chatwoot_inbox_id || 0))
                  .map((chat) => ({ value: chat.id, label: chat.name }))
                }
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Search Controls */}
          <div className="space-y-6">
            {/* Collection Selection */}
            <div>
              <label className="block text-xs font-semibold mb-3" style={{ color: "var(--color-text-secondary)" }}>
                {translate("selectCollection") || "Select Collection"}
              </label>
              <div className="grid grid-cols-1 gap-3">
                {collections.map((collection) => {
                  const Icon = collection.icon;
                  return (
                    <button
                      key={collection.id}
                      onClick={() => setSelectedCollection(collection.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all duration-200`}
                      style={
                        selectedCollection === collection.id
                          ? { borderColor: "var(--color-mint)", backgroundColor: "var(--color-mint-muted)" }
                          : { borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)" }
                      }
                    >
                      <div className="flex items-start space-x-3">
                        <Icon
                          className="h-5 w-5 mt-0.5"
                          style={
                            selectedCollection === collection.id
                              ? { color: "var(--color-mint)" }
                              : { color: "var(--color-text-muted)" }
                          }
                        />
                        <div className="flex-1">
                          <h3
                            className="text-sm font-semibold"
                            style={selectedCollection === collection.id ? { color: "var(--color-mint-dark)" } : { color: "var(--color-text-secondary)" }}
                          >
                            {collection.name}
                          </h3>
                          <p
                            className="text-sm"
                            style={selectedCollection === collection.id ? { color: "var(--color-text-secondary)" } : { color: "var(--color-text-muted)" }}
                          >
                            {collection.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Search Query */}
            <div>
              <label className="block text-xs font-semibold mb-2" style={{ color: "var(--color-text-secondary)" }}>
                {translate("searchQuery") || "Search Query"}
              </label>
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  translate("enterSearchQuery") || "Enter a search query..."
                }
                className="field-input w-full resize-none"
                rows={3}
              />
            </div>

            {/* Results Limit */}
            <div>
              <label className="block text-xs font-semibold mb-2" style={{ color: "var(--color-text-secondary)" }}>
                {translate("resultsLimit") || "Results Limit"}: {limit}
              </label>
              <input
                type="range"
                min="1"
                max="20"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value))}
                className="w-full h-2 rounded-xl appearance-none cursor-pointer"
                style={{
                  accentColor: "var(--color-mint)",
                  background: `linear-gradient(to right, var(--color-mint) 0%, var(--color-mint) ${((limit - 1) / 19) * 100}%, var(--color-border) ${((limit - 1) / 19) * 100}%, var(--color-border) 100%)`,
                }}
              />
              <div className="flex justify-between text-xs text-[var(--color-text-muted)] mt-1">
                <span>1</span>
                <span>20</span>
              </div>
            </div>

            {/* Raw Search Toggle (Products only) */}
            {selectedCollection === "products" && (
              <div className="flex items-center justify-between">
                <label className="block text-xs font-semibold" style={{ color: "var(--color-text-secondary)" }}>
                  {translate("rawSearch") || "Raw Search"}
                </label>
                <button
                  type="button"
                  onClick={() => setRawSearch(!rawSearch)}
                  className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out"
                  style={{ backgroundColor: rawSearch ? "var(--color-mint)" : "var(--color-border)" }}
                  role="switch"
                  aria-checked={rawSearch}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-[var(--color-surface)] shadow ring-0 transition duration-200 ease-in-out ${
                      rawSearch ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
            )}

            {/* Product Filters (Products collection only, non-raw search) */}
            {selectedCollection === "products" && !rawSearch && (
              <div className="space-y-3 p-3 rounded-lg" style={{ backgroundColor: "var(--color-surface-secondary)", border: "1px solid var(--color-border)" }}>
                <label className="block text-xs font-semibold" style={{ color: "var(--color-text-secondary)" }}>
                  {translate("searchFilters") || "Search Filters"}
                </label>

                {/* Price Range */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs mb-1" style={{ color: "var(--color-text-muted)" }}>
                      {translate("minPrice") || "Min Price (€)"}
                    </label>
                    <input
                      type="number"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      placeholder="0"
                      min="0"
                      className="w-full px-2 py-1.5 rounded text-sm"
                      style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)", color: "var(--color-text)" }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: "var(--color-text-muted)" }}>
                      {translate("maxPrice") || "Max Price (€)"}
                    </label>
                    <input
                      type="number"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      placeholder="∞"
                      min="0"
                      className="w-full px-2 py-1.5 rounded text-sm"
                      style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)", color: "var(--color-text)" }}
                    />
                  </div>
                </div>

                {/* Brands */}
                <div>
                  <label className="block text-xs mb-1" style={{ color: "var(--color-text-muted)" }}>
                    {translate("brands") || "Brands (comma-separated)"}
                  </label>
                  <input
                    type="text"
                    value={brands}
                    onChange={(e) => setBrands(e.target.value)}
                    placeholder="Samsung, LG, Bosch"
                    className="w-full px-2 py-1.5 rounded text-sm"
                    style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)", color: "var(--color-text)" }}
                  />
                </div>

                {/* Availability & Price Preference */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs mb-1" style={{ color: "var(--color-text-muted)" }}>
                      {translate("availability") || "Availability"}
                    </label>
                    <select
                      value={availability}
                      onChange={(e) => setAvailability(e.target.value)}
                      className="w-full px-2 py-1.5 rounded text-sm"
                      style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)", color: "var(--color-text)" }}
                    >
                      <option value="">{translate("any") || "Any"}</option>
                      <option value="in_stock">{translate("inStock") || "In Stock"}</option>
                      <option value="out_of_stock">{translate("outOfStock") || "Out of Stock"}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: "var(--color-text-muted)" }}>
                      {translate("pricePreference") || "Price Preference"}
                    </label>
                    <select
                      value={pricePreference}
                      onChange={(e) => setPricePreference(e.target.value)}
                      className="w-full px-2 py-1.5 rounded text-sm"
                      style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)", color: "var(--color-text)" }}
                    >
                      <option value="">{translate("any") || "Any"}</option>
                      <option value="lowest">{translate("cheapest") || "Cheapest"}</option>
                      <option value="highest">{translate("mostExpensive") || "Most Expensive"}</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Search Button */}
            <button
              onClick={handleSearch}
              disabled={isLoading || !query.trim()}
              className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              ) : (
                <Search className="h-4 w-4" />
              )}
              <span>{translate("search") || "Search"}</span>
            </button>
          </div>

          {/* Results */}
          <div className="h-[600px] flex flex-col">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full text-[var(--color-text-secondary)]">
                <div style={{ width: '32px', height: '32px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: 16 }} />
                <p className="text-sm">{translate("searching") || "Searching..."}</p>
              </div>
            ) : results.length > 0 ? (
              <div className="flex flex-col h-full">
                <h3 className="font-semibold text-base text-[var(--color-text-primary)] mb-4">
                  {translate("searchResults") || "Search Results"} (
                  {results.length})
                </h3>
                <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                  {results.map((result, index) => (
                    <div
                      key={result.id || index}
                      className="border border-[var(--color-border)] rounded-xl p-4 transition-colors cursor-pointer"
                      style={{ backgroundColor: "var(--color-surface)" }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--color-mint-muted)"; e.currentTarget.style.borderColor = "var(--color-mint-border)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--color-surface)"; e.currentTarget.style.borderColor = "var(--color-border)"; }}
                      onClick={() => handleResultClick(result)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2 py-0.5 border border-[var(--color-border)] rounded text-xs text-[var(--color-text-secondary)]">
                            #{index + 1}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 border rounded text-xs badge-mint">
                            {formatScore(result.score)}% match
                          </span>
                        </div>
                      </div>
                      {selectedCollection === "products" ? (
                        <>
                          <p className="text-sm font-medium text-[var(--color-text-primary)] mb-1">
                            {result.metadata?.displayText || result.text || `Product ${index + 1}`}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            {result.metadata?.Marca && (
                              <span className="px-2 py-0.5 rounded" style={{ backgroundColor: "var(--color-mint-muted)", color: "var(--color-mint-dark)" }}>
                                {result.metadata.Marca}
                              </span>
                            )}
                            {result.metadata?.price && (
                              <span className="font-semibold text-[var(--color-success)]">
                                €{result.metadata.price}
                              </span>
                            )}
                            {result.metadata?.pvpr && result.metadata.pvpr > result.metadata.price && (
                              <span className="text-[var(--color-text-muted)] line-through">
                                €{result.metadata.pvpr}
                              </span>
                            )}
                            {result.metadata?.pvpr_perc_desconto && (
                              <span className="px-1.5 py-0.5 rounded bg-[var(--color-error-muted)] text-[var(--color-error)]">
                                {result.metadata.pvpr_perc_desconto}
                              </span>
                            )}
                          </div>
                          {result.metadata?.pontos_fortes && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {(Array.isArray(result.metadata.pontos_fortes)
                                ? result.metadata.pontos_fortes
                                : typeof result.metadata.pontos_fortes === "string"
                                  ? result.metadata.pontos_fortes.split(",")
                                  : [String(result.metadata.pontos_fortes)]
                              ).slice(0, 4).map((feature, i) => (
                                <span
                                  key={i}
                                  className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                                  style={{
                                    backgroundColor: "var(--color-surface-raised)",
                                    color: "var(--color-text-secondary)",
                                    border: "1px solid var(--color-border)",
                                  }}
                                >
                                  {typeof feature === "string" ? feature.trim() : feature}
                                </span>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="text-sm text-[var(--color-text-secondary)] mb-2">
                            {truncateText(result.text || "", 300)}
                          </div>
                          {result.metadata && (
                            <div className="text-xs text-[var(--color-text-muted)]">
                              {result.metadata.file_name && (
                                <span className="mr-4">
                                  <strong className="text-[var(--color-text-secondary)]">{translate("file") || "File"}:</strong>{" "}
                                  {result.metadata.file_name}
                                </span>
                              )}
                              {result.metadata.content_type && (
                                <span>
                                  <strong className="text-[var(--color-text-secondary)]">{translate("type") || "Type"}:</strong>{" "}
                                  {result.metadata.content_type}
                                </span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-full" style={{ color: "var(--color-error)" }}>
                <div className="text-center">
                  <p className="text-lg font-semibold mb-2">
                    {translate("error") || "Error"}
                  </p>
                  <p className="text-sm">{error}</p>
                </div>
              </div>
            ) : hasSearched ? (
              <div className="flex flex-col items-center justify-center h-full text-[var(--color-text-secondary)]">
                <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 border border-[var(--color-border)]" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                  <Search className="h-8 w-8 text-[var(--color-text-muted)]" />
                </div>
                <h3 className="font-semibold text-base text-[var(--color-text-primary)] mb-2">
                  {translate("noResultsFound") || "No Results Found"}
                </h3>
                <p className="text-sm text-[var(--color-text-secondary)] text-center">
                  {translate("noResultsDescription") ||
                    "Try adjusting your search terms or try a different collection"}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-[var(--color-text-secondary)]">
                <Search className="h-12 w-12 mb-4 text-[var(--color-text-muted)]" />
                <h3 className="font-semibold text-base text-[var(--color-text-primary)] mb-2">
                  {translate("readyToSearch") || "Ready to Search"}
                </h3>
                <p className="text-sm text-[var(--color-text-secondary)] text-center">
                  {translate("readyToSearchDescription") ||
                    "Try searching for content, descriptions, or features"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Result Details Modal */}
      {showModal && selectedResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            ref={modalRef}
            className="card rounded-xl border border-[var(--color-border)] max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6 border-b border-[var(--color-border)] pb-4">
                <h2 className="text-2xl font-semibold text-[var(--color-text-primary)]">
                  {translate("resultDetails") || "Result Details"}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="p-4 rounded-xl border border-[var(--color-mint-border)]" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                  <h3 className="font-semibold text-base text-[var(--color-text-primary)] mb-3">
                    {translate("basicInformation") || "Basic Information"}
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-[var(--color-text-secondary)]">
                        {translate("similarityScore") || "Similarity Score"}:
                      </span>
                      <span className="text-sm font-semibold" style={{ color: "var(--color-mint-dark)" }}>
                        {selectedResult.score}
                      </span>
                    </div>
                    {selectedCollection === "products" ? (
                      <>
                        {selectedResult.metadata?.price && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-[var(--color-text-secondary)]">{translate("price") || "Price"}:</span>
                            <div className="flex items-center gap-2">
                              {selectedResult.metadata?.pvpr && selectedResult.metadata.pvpr > selectedResult.metadata.price && (
                                <span className="text-sm text-[var(--color-text-muted)] line-through">€{selectedResult.metadata.pvpr}</span>
                              )}
                              <span className="text-sm font-semibold text-[var(--color-success)]">€{selectedResult.metadata.price}</span>
                              {selectedResult.metadata?.pvpr_perc_desconto && (
                                <span className="text-xs bg-[var(--color-error-muted)] text-[var(--color-error)] px-1.5 py-0.5 rounded">{selectedResult.metadata.pvpr_perc_desconto}</span>
                              )}
                            </div>
                          </div>
                        )}
                        {selectedResult.metadata?.Marca && (
                          <div className="flex justify-between">
                            <span className="text-sm text-[var(--color-text-secondary)]">{translate("brand") || "Brand"}:</span>
                            <span className="text-sm text-[var(--color-text-primary)]">{selectedResult.metadata.Marca}</span>
                          </div>
                        )}
                        {selectedResult.metadata?.Modelo && (
                          <div className="flex justify-between">
                            <span className="text-sm text-[var(--color-text-secondary)]">{translate("model") || "Model"}:</span>
                            <span className="text-sm text-[var(--color-text-primary)]">{selectedResult.metadata.Modelo}</span>
                          </div>
                        )}
                        {selectedResult.metadata?.stock !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-sm text-[var(--color-text-secondary)]">{translate("stock") || "Stock"}:</span>
                            <span className="text-sm text-[var(--color-text-primary)]">{selectedResult.metadata.stock} {translate("units") || "units"}</span>
                          </div>
                        )}
                        {selectedResult.metadata?.availibility && (
                          <div className="flex justify-between">
                            <span className="text-sm text-[var(--color-text-secondary)]">{translate("availability") || "Availability"}:</span>
                            <span className="text-sm text-[var(--color-text-primary)]">{selectedResult.metadata.availibility}</span>
                          </div>
                        )}
                        {selectedResult.metadata?.cod_produto_sap && (
                          <div className="flex justify-between">
                            <span className="text-sm text-[var(--color-text-secondary)]">{translate("sapCode") || "SAP Code"}:</span>
                            <span className="text-sm text-[var(--color-text-primary)] font-mono">{selectedResult.metadata.cod_produto_sap}</span>
                          </div>
                        )}
                        {selectedResult.metadata?.url && /^https?:\/\//i.test(selectedResult.metadata.url) && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-[var(--color-text-secondary)]">{translate("productUrl") || "URL"}:</span>
                            <a href={selectedResult.metadata.url} target="_blank" rel="noopener noreferrer" className="btn-primary text-xs">
                              {translate("viewProduct") || "View Product"}
                            </a>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {selectedResult.metadata?.file_name && (
                          <div className="flex justify-between">
                            <span className="text-sm text-[var(--color-text-secondary)]">{translate("file") || "File"}:</span>
                            <span className="text-sm text-[var(--color-text-primary)]">{selectedResult.metadata.file_name}</span>
                          </div>
                        )}
                        {selectedResult.metadata?.content_type && (
                          <div className="flex justify-between">
                            <span className="text-sm text-[var(--color-text-secondary)]">{translate("type") || "Type"}:</span>
                            <span className="text-sm text-[var(--color-text-primary)]">{selectedResult.metadata.content_type}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {selectedCollection === "products" && selectedResult.metadata?.imageUrl && (
                    <div className="mt-4">
                      <img
                        src={selectedResult.metadata.imageUrl}
                        alt={selectedResult.metadata?.displayText || "Product"}
                        className="w-full h-32 object-contain bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)]"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </div>
                  )}
                </div>

                {/* Key Features (Products only) */}
                {selectedCollection === "products" && selectedResult.metadata?.pontos_fortes && (
                  <div className="p-4 rounded-xl border border-[var(--color-mint-border)]" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                    <h3 className="font-semibold text-base text-[var(--color-text-primary)] mb-3">
                      {translate("keyFeatures") || "Key Features"}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {(Array.isArray(selectedResult.metadata.pontos_fortes)
                        ? selectedResult.metadata.pontos_fortes
                        : typeof selectedResult.metadata.pontos_fortes === "string"
                          ? selectedResult.metadata.pontos_fortes.split(",")
                          : [String(selectedResult.metadata.pontos_fortes)]
                      ).map((feature, i) => (
                        <span key={i} className="badge-mint px-2 py-1 rounded text-sm">
                          {typeof feature === "string" ? feature.trim() : feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="p-4 rounded-xl border border-[var(--color-border)]" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                  <h3 className="font-semibold text-base text-[var(--color-text-primary)] mb-3">
                    {selectedCollection === "products"
                      ? translate("embeddedText") || "Embedded Text"
                      : translate("content") || "Content"}
                  </h3>
                  <div className="p-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
                    <p className="text-sm text-[var(--color-text-secondary)] whitespace-pre-wrap font-mono break-words">
                      {selectedResult.text}
                    </p>
                  </div>
                </div>

                {/* Metadata */}
                {selectedResult.metadata && (
                  <div className="p-4 rounded-xl border border-[var(--color-border)]" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                    <h3 className="font-semibold text-base text-[var(--color-text-primary)] mb-3">
                      {translate("metadata") || "Metadata"}
                    </h3>
                    <pre className="text-xs text-[var(--color-text-muted)] overflow-x-auto">
                      {JSON.stringify(selectedResult.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleCloseModal}
                  className="btn-secondary"
                >
                  {translate("close") || "Close"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimilaritySearch;
